import { createContext, type ReactNode, useContext, useEffect, useMemo } from "react";
import { bgAudio } from "./assets";
import { listenForPresses } from "./muteOnPress";
import { useBackgroundAudio } from "./useBackgroundAudio";

/**
 * The looping `<audio>` used to live inside `Home`, which meant navigating to
 * /watch, /live or /contact unmounted it: the loop stopped, and coming back
 * mounted a fresh, muted element that started from the top. Ticket 013.
 *
 * So it is mounted here instead, wrapped around the route swap in `App` — the
 * one place nothing a page or a popup does can unmount. `#bg-audio` therefore
 * exists on every route, which `scripts/behaviour.ts` relies on, and both the
 * mute state and the playback position now survive navigation.
 *
 * The controls travel by context because `ROUTES` maps to zero-prop
 * components: Home's mute toggle and the two popups all sit below `App` with
 * no prop path down to them.
 */

export type BackgroundAudio = {
  muted: boolean;
  toggleMute: () => void;
  /** Pause an audible loop so another track can play; a no-op if it is muted. */
  duck: () => void;
  unduck: () => void;
};

/**
 * Exported for one reason: the provider always starts muted with a real
 * `<audio>` behind it, so it cannot show a test what the unmuted control looks
 * like or observe a click reaching `toggleMute`. Pages read it through
 * `useBackgroundAudioContext` below, never directly.
 */
export const BackgroundAudioContext = createContext<BackgroundAudio | null>(null);

export function BackgroundAudioProvider({ children }: { children?: ReactNode }) {
  const { audioRef, muted, toggleMute, muteIfAudible, duck, unduck } = useBackgroundAudio();

  // Ticket 028. Here rather than on each object for the same reason the
  // `<audio>` is here: this is the one place nothing a page or a popup does can
  // unmount, so the listener outlives every route swap. `muteIfAudible` is
  // created once, so this attaches once too.
  useEffect(() => listenForPresses(document, muteIfAudible), [muteIfAudible]);

  // Only `muted` ever changes; the three callbacks are created once.
  const value = useMemo(
    () => ({ muted, toggleMute, duck, unduck }),
    [muted, toggleMute, duck, unduck],
  );

  return (
    <BackgroundAudioContext.Provider value={value}>
      {children}
      {/* Ogg Opus only — browsers without it (Safari < 17) are out of scope. */}
      <audio ref={audioRef} id="bg-audio" loop autoPlay muted>
        <source src={bgAudio} type="audio/ogg; codecs=opus" />
      </audio>
    </BackgroundAudioContext.Provider>
  );
}

export function useBackgroundAudioContext(): BackgroundAudio {
  const value = useContext(BackgroundAudioContext);
  // Falling back to no-ops would mean a mute button that silently does nothing,
  // which is exactly the failure this ticket exists to remove.
  if (!value) {
    throw new Error("useBackgroundAudioContext used outside <BackgroundAudioProvider>");
  }
  return value;
}
